import { useEffect, useState } from "react";
import { downloadStaffStockPrintInfo, postPrintStockQrAPI, postStaffStockSearchInfoListAPI } from "../../../../http/api/StaffStockAPI";
import useInputObject from "../../../../hooks/useInputObject";
import { getHyphenedDate, inputValueToIntOrNull } from "../../../../utils/utils";

/** 担当者在庫一覧１ページ行数 */
const STAFF_STOCK_ROW_COUNT_A_PAGE = 30;

/**
 * 担当者在庫一覧画面情報を保持するhook
 */
const useStaffStockPageInfo = () => {

  /**
   * 担当者在庫情報のリストを保持するState
   */
  const [staffStockInfoList, setStaffStockInfoList] = useState([]);

  /**
   * 担当者在庫一覧の検索条件を保持するState
   */
  const searchCondition = useInputObject({
    staffId: "",
    commodityOwner: "",
    product: "",
    salesRecordNo: "",
    totalDate: getHyphenedDate(new Date()),
    includesNotAvailableStock: false,
    includesOnlyChangedToday: false
  });
  /**
   * 担当者在庫一覧の検索した条件を保持するState
   */
  const searchedCondition = useInputObject({
    staffId: "",
    commodityOwner: "",
    product: "",
    salesRecordNo: "",
    totalDate: getHyphenedDate(new Date()),
    includesNotAvailableStock: false
  });

  /** 担当者在庫情報テーブルのページングを保持するstate */
  const [pagination, setPagination] = useState({
    page: 0,
    pageSize: STAFF_STOCK_ROW_COUNT_A_PAGE,
    totalPage: 0,
  })

  /** 「取得中」モーダルの表示状態を保持するstate */
  const [isLoading, setLoading] = useState(false);

  /** 印刷プレビューのダイアログの状態を保持するstate */
  const printPreview = useInputObject({
    open: false,
    url: "",
  });

  useEffect(() => {
    (async () => {
      search();
    })();
  },[]);
  
  /** 検索を実行する */
  const searchStaffStockInfo = async (searchConditionValue)=> {
    let staffStockInfoRes = await fetchStaffStockInfo(
      formatSearchConditionInput(searchConditionValue)
    );

    setStaffStockInfoList(
      staffStockInfoRes.staffStockInfoList
    );

    setPagination((prev) => ({
      ...prev,
      page: 0,
      totalPage: Math.ceil(staffStockInfoRes.staffStockInfoList.length / STAFF_STOCK_ROW_COUNT_A_PAGE),
    }));
  }

  /** ページで検索する */
  const movePage = async (page) => {
    setPagination((prev) => ({
      ...prev,
      page
    }));
  }

  /** 検索する */
  const search = async () => {
    //検索した条件保存する
    searchedCondition.setValue(searchCondition.Value)
    searchStaffStockInfo(searchCondition.Value);
  }

  /** 集計日時を変更する */
  const changeTotalDate = async (totalDate) => {
    const newCondition = {...searchCondition.Value, totalDate};
    searchCondition.updates(newCondition);
    searchStaffStockInfo(newCondition, 0);
  };

  /** 担当者別在庫一覧の帳票を印刷する */
  const printStaffStock = async () => {
    try {
      //　ローディング　表示
      setLoading(true);
      // request to server 
      // request body 宣言
      const requestBody = searchedCondition.Value;
      // download 結果　受け取る
      const result = await downloadStaffStockPrintInfo(requestBody);
      //印刷情報設定
      if (result) {
        const blob = new Blob([result], {
          type: "application/pdf",
          encoding: "UTF-8",
        });
        const fileUrl = URL.createObjectURL(blob);
        printPreview.updates({ open: true, url: fileUrl });
      }
    } catch (error) {

    }
    finally {
      //　ローディング　非表示
      setLoading(false);
    }

  };

    /** 在庫のQRを印刷する */
  const printStockQR = async (selectedSalesRecordNos) => {
    try {
      //　ローディング　表示
      setLoading(true);
      // request to server 
      // request body 宣言
      const requestBody = {
        ...searchedCondition.Value,
        selectedSalesRecordNos
      }
      // download 結果　受け取る
      const result = await postPrintStockQrAPI(requestBody);

      //印刷情報設定
      if (result) {
        const blob = new Blob([result], {
          type: "application/pdf",
          encoding: "UTF-8",
        });
              
        const fileUrl = URL.createObjectURL(blob);
        printPreview.updates({ open: true, url: fileUrl });
      }
    } catch (error) {
      throw(error);
    }
    finally {
      //　ローディング　非表示
      setLoading(false);
    }

  };

  const resetToPreviousSearchCondition = () => {
    searchCondition.setValue(searchedCondition.Value);
  }

  return {
    staffStockInfoList,
    searchCondition,
    searchedCondition,
    pagination,
    isLoading,
    printPreview,
    search,
    movePage,
    changeTotalDate,
    printStaffStock,
    resetToPreviousSearchCondition,
    printStockQR
  }
}

export default useStaffStockPageInfo;

/** 担当者在庫をサーバから取得する */
const fetchStaffStockInfo = async (searchCondition) => {

  const res = await postStaffStockSearchInfoListAPI({
    ...searchCondition
  });

  return res;
}

/** 検索値の成型 */
const formatSearchConditionInput = (searchConditionValue) => ({
  ...searchConditionValue,
  staffId: inputValueToIntOrNull(searchConditionValue.staffId),
  salesRecordNo: inputValueToIntOrNull(searchConditionValue.salesRecordNo),
});
